/**
 * Magic.Lap theme system — 3-state toggle: light → dark → auto → light.
 *
 * - Preference stored in localStorage.theme ('light' | 'dark' | 'auto').
 * - 'auto' resolves via prefers-color-scheme and updates reactively.
 * - Default on first visit: 'light' (no OS fallback per spec).
 * - DOM: syncs both .dark class AND data-theme attr on <html>.
 * - Also updates <meta name="theme-color"> for mobile chrome.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

export type Theme = 'light' | 'dark' | 'auto';
export type ResolvedTheme = 'light' | 'dark';

const CYCLE: Theme[] = ['light', 'dark', 'auto'];

const THEME_COLOR: Record<ResolvedTheme, string> = {
  light: '#EFF1EC', // --bg-canvas light (M3 surface-container)
  dark:  '#111412', // --bg-canvas dark (M3 surface)
};

interface ThemeContextValue {
  /** The stored preference (may be 'auto'). */
  theme: Theme;
  /** The actually-applied theme ('light' or 'dark'). */
  resolved: ResolvedTheme;
  /** Set a specific theme. */
  setTheme: (t: Theme) => void;
  /** Cycle: light → dark → auto → light. */
  cycle: () => void;
}

const ThemeCtx = createContext<ThemeContextValue>({
  theme: 'light',
  resolved: 'light',
  setTheme: () => {},
  cycle: () => {},
});

function getSystemPreference(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(theme: Theme): ResolvedTheme {
  return theme === 'auto' ? getSystemPreference() : theme;
}

function applyToDOM(resolved: ResolvedTheme): void {
  const root = document.documentElement;

  if (resolved === 'dark') {
    root.classList.add('dark');
    root.setAttribute('data-theme', 'dark');
  } else {
    root.classList.remove('dark');
    root.setAttribute('data-theme', 'light');
  }

  // Update mobile browser chrome color
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.content = THEME_COLOR[resolved];
}

function readStored(): Theme {
  try {
    const stored = localStorage.getItem('theme');
    if (stored === 'light' || stored === 'dark' || stored === 'auto') return stored;
  } catch {
    // localStorage unavailable (private mode, etc.)
  }
  return 'light'; // Default: light on first visit
}

function writeStored(theme: Theme): void {
  try {
    localStorage.setItem('theme', theme);
  } catch {
    // ignore
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStored);

  const resolved = resolveTheme(theme);

  // Apply to DOM whenever resolved changes
  useEffect(() => {
    applyToDOM(resolved);
  }, [resolved]);

  // When 'auto', watch system preference and re-apply live
  useEffect(() => {
    if (theme !== 'auto') return;

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyToDOM(resolveTheme('auto'));
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  function setTheme(t: Theme) {
    writeStored(t);
    setThemeState(t);
  }

  function cycle() {
    const next = CYCLE[(CYCLE.indexOf(theme) + 1) % CYCLE.length];
    setTheme(next);
  }

  return (
    <ThemeCtx.Provider value={{ theme, resolved, setTheme, cycle }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeCtx);
}
